import React from 'react';
import { ArrowRight, Star, Calendar, CheckCircle, ChevronDown } from 'lucide-react';
import { businessInfo } from '../mock';
import { useSale } from '../context/SaleContext';

const Hero = () => {
  const { isBannerVisible } = useSale();

  const handleBookNow = () => {
    const element = document.querySelector('#booking-walkthrough');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleCallToBook = () => {
    window.location.href = `tel:${businessInfo.phone}`;

    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('track', 'Contact', {
        content_category: 'Phone Call',
        content_name: 'Hero CTA - Call to Book'
      });
    }
  };

  const handleScrollDown = () => {
    const element = document.querySelector('#booking-walkthrough');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const saleBannerOffset = isBannerVisible ? 'pt-10 md:pt-0' : '';

  return (
    <section className={`hero-section relative min-h-screen flex items-center justify-center overflow-hidden ${saleBannerOffset}`}>
      {/* Background Video */}
      <div className="absolute inset-0 w-full h-full">
        <video
          data-testid="hero-bg-video"
          className="w-full h-full object-cover"
          src="https://customer-assets-v7afamib.emergentagent.net/job_eddf8515-2938-471a-a6e5-081675307203/artifacts/dmi1rxyt_12370189-hd_720_1280_30fps.mp4"
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          poster="https://images.unsplash.com/photo-1629281066736-ff3a1e6b36d8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBjYXIlMjBkZXRhaWxpbmd8ZW58MHx8fHwxNzU4NjYxMzA3fDA&ixlib=rb-4.1.0&q=85"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent"></div>
      </div>

      {/* Hero Content */}
      <div className="container relative z-10 pt-6 md:pt-20 px-4 md:px-8">
        <div className="max-w-4xl">
          {/* Badge */}
          <div className="inline-block mb-4 md:mb-6 px-4 md:px-5 py-2 bg-blue-500/20 backdrop-blur-sm rounded-full border border-blue-400/30">
            <span className="text-blue-300 text-xs md:text-sm font-semibold uppercase tracking-wider flex items-center gap-2">
              <Star size={14} className="fill-blue-400 text-blue-400 md:w-4 md:h-4" />
              Mobile Service
            </span>
          </div>

          <h1 className="brand-display text-white mb-4 md:mb-6 leading-tight text-3xl sm:text-4xl md:text-5xl lg:text-6xl">
            Premium Mobile Car Detailing
          </h1>

          {/* SEO-only copy — visible to crawlers, hidden from users */}
          <p className="sr-only">
            {businessInfo.tagline}
          </p>
          <p className="sr-only">
            Professional mobile car detailing that comes to you. Ceramic coating protection,
            interior & exterior detailing in Abbotsford and surrounding areas.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 mb-8 md:mb-12">
            <button
              onClick={handleBookNow}
              className="btn-primary flex items-center justify-center space-x-2 text-base md:text-lg px-6 md:px-8 py-3 md:py-4"
            >
              <Calendar size={18} className="md:w-5 md:h-5" />
              <span>Book Now</span>
              <ArrowRight size={18} className="md:w-5 md:h-5" />
            </button>

            <button
              onClick={handleCallToBook}
              className="btn-secondary flex items-center justify-center space-x-2 text-base md:text-lg px-6 md:px-8 py-3 md:py-4"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="md:w-5 md:h-5">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
              </svg>
              <span>Call to Book</span>
            </button>
          </div>

          {/* Trust Indicators */}
          <div className="flex flex-col md:flex-row md:flex-wrap items-start md:items-center gap-3 md:gap-8 text-xs md:text-sm">
            <div className="flex items-center gap-2">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={16} className="fill-yellow-400 text-yellow-400 md:w-[18px] md:h-[18px]" />
                ))}
              </div>
              <span className="text-gray-300 font-semibold">5.0 Rating</span>
            </div>

            <div className="flex items-center gap-2 text-gray-300">
              <CheckCircle size={16} className="text-blue-400 md:w-[18px] md:h-[18px]" />
              <span className="font-semibold">Professional Grade Products</span>
            </div>

            <div className="flex items-center gap-2 text-gray-300">
              <CheckCircle size={16} className="text-blue-400 md:w-[18px] md:h-[18px]" />
              <span className="font-semibold">Ceramic Coating Specialists</span>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Down Indicator */}
      <div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 cursor-pointer"
        onClick={handleScrollDown}
      >
        <ChevronDown className="w-6 h-6 text-gray-400 hover:text-white transition-colors animate-bounce" />
      </div>

      {/* Bottom Gradient Fade */}
      <div className="absolute bottom-0 left-0 right-0 h-56 md:h-32 bg-gradient-to-t from-black via-black/90 to-transparent pointer-events-none"></div>
    </section>
  );
};

export default Hero;
